import React, { useState } from 'react';
import '../styles/Tables.css';
import { useTranslation } from 'react-i18next';

function BenchmarkSection({ portfolioPercentage, ndaqPercentage, spyPercentage, arkkPercentage }) {
  const { t } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: 'value', direction: 'desc' });
  
  const formatPercentage = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'N/A';
    }
    return `${value > 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;
  };
  
  // Create benchmark data array for easier manipulation
  const benchmarkData = [
    {
      id: 'ampyfin',
      name: 'AmpyFin',
      value: portfolioPercentage,
      icon: '🚀',
      color: 'primary',
      description: t('benchmark.ampyfinDescription', 'Our AI-powered trading system')
    },
    {
      id: 'qqq',
      name: 'QQQ',
      value: ndaqPercentage,
      icon: '📊',
      color: 'success',
      description: t('benchmark.qqqDescription', 'Invesco QQQ Trust ETF')
    },
    {
      id: 'spy',
      name: 'SPY',
      value: spyPercentage,
      icon: '📈',
      color: 'info',
      description: t('benchmark.spyDescription', 'SPDR S&P 500 ETF Trust')
    },
    {
      id: 'arkk',
      name: 'ARKK',
      value: arkkPercentage,
      icon: '🔬',
      color: 'warning',
      description: t('benchmark.arkkDescription', 'ARK Innovation ETF')
    }
  ];
  
  // Sorting function
  const sortData = (data, key, direction) => {
    return [...data].sort((a, b) => {
      let aVal = a[key];
      let bVal = b[key];
      
      if (typeof aVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      
      if (aVal < bVal) return direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return direction === 'asc' ? 1 : -1;
      return 0;
    });
  };
  
  // Handle sort
  const handleSort = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  
  // Sort data
  const sortedData = sortConfig.key ? sortData(benchmarkData, sortConfig.key, sortConfig.direction) : benchmarkData;
  
  return (
    <div className="fancy-portfolio-container">
      
      {/* Performance Summary */}
      <div className="performance-summary">
        <div className="summary-content">
          <span className="summary-icon">📊</span>
          <div className="summary-text">
            <p className="summary-message">{t('benchmark.summaryMessage', 'Live performance comparison since launch')}</p>
            <div className="summary-stats">
              <span className="summary-stat">
                <strong>{t('benchmark.launchDate', 'Launch Date')}:</strong> {t('benchmark.launchDateValue', 'Jan 3, 2025 @ 9:30 AM')}
              </span>
              <span className="summary-stat">
                <strong>{t('benchmark.updateFrequency', 'Updates')}:</strong> {t('benchmark.updateFrequencyValue', 'Real-time')}
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Enhanced Benchmark Cards */}
      <div className="benchmark-cards-container">
        <div className="benchmark-cards-header">
          <h3 className="cards-title">{t('benchmark.performanceComparison', 'Performance Comparison')}</h3>
          <div className="sort-controls">
            <button 
              className={`sort-btn ${sortConfig.key === 'value' ? 'active' : ''}`}
              onClick={() => handleSort('value')}
            >
              {t('benchmark.sortByPerformance', 'Sort by Performance')}
              {sortConfig.key === 'value' && (
                <span className="sort-indicator">
                  {sortConfig.direction === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </button>
          </div>
        </div>
        
        <div className="benchmark-cards-grid">
          {sortedData.map((benchmark, index) => (
            <div 
              key={benchmark.id} 
              className={`benchmark-card ${benchmark.value > 0 ? 'positive' : 'negative'} ${index === 0 ? 'top-performer' : ''}`}
            >
              <div className="card-header">
                <div className="card-icon">
                  <span className="icon-emoji">{benchmark.icon}</span>
                </div>
                <div className="card-title">
                  <h4 className="benchmark-name">{benchmark.name}</h4>
                  <p className="benchmark-description">{benchmark.description}</p>
                </div>
                {index === 0 && (
                  <div className="top-badge">
                    <span className="top-icon">🏆</span>
                  </div>
                )}
              </div>
              
              <div className="card-body">
                <div className="performance-value">
                  <span className={`value-text ${benchmark.value > 0 ? 'positive' : 'negative'}`}>
                    {formatPercentage(benchmark.value)}
                  </span>
                  <span className="value-label">{t('benchmark.totalReturn', 'Total Return')}</span>
                </div>
                
                <div className="performance-details">
                  <div className="detail-item">
                    <span className="detail-label">{t('benchmark.rank', 'Rank')}</span>
                    <span className="detail-value">#{index + 1}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{t('benchmark.status', 'Status')}</span>
                    <span className={`status-badge ${benchmark.value > 0 ? 'profitable' : 'loss'}`}>
                      {benchmark.value > 0 ? t('benchmark.profitable', 'Profitable') : t('benchmark.loss', 'Loss')}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="card-footer">
                <span className="time-period">{t('benchmark.totalPercentageSince')}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Benchmark Footer */}
      <div className="portfolio-footer">
        <div className="footer-info">
          <span className="footer-text">
            {t('benchmark.lastUpdated', 'Last Updated')}: {new Date().toLocaleString()}
          </span>
        </div>
        <div className="footer-actions">
          <button className="footer-btn" onClick={() => window.location.reload()}>
            <span className="btn-icon">🔄</span>
            <span className="btn-text">{t('benchmark.refresh', 'Refresh')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default BenchmarkSection; 